import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Share2, Instagram, ShoppingBag, X, Loader2 } from "lucide-react";
import { COLORS, colorById } from "../lib/colors";
import { BagViewer } from "../components/BagViewer";
import { ColorPicker } from "../components/ColorPicker";
import { SimulatedAR } from "../components/SimulatedAR";
import { SpecSheet } from "../components/SpecSheet";
import { NativeAR, tryActivateAR } from "../components/NativeAR";
import { Nav } from "../components/Nav";
import { PageFooter } from "../components/PageFooter";
import { toast } from "sonner";
import { shareOrDownload } from "../lib/share";
import { track, Events } from "../lib/analytics";
import { tap as hapticTap, success as hapticSuccess, selection as hapticSelection } from "../lib/haptics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/kisubikashop";

const ARPreview = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const initial = useMemo(
    () => colorById(state?.colorId || "klein-blue"),
    [state]
  );
  const [color, setColor] = useState(initial);
  const [arOpen, setArOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", shipping_address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    setColor(initial);
  }, [initial]);

  const launchAR = async () => {
    hapticTap("medium");
    track(Events.ARLaunched, { color_id: color.id, color_name: color.name });
    // Try true scene-anchored AR (Android Scene Viewer / WebXR) first.
    // Falls back to simulated camera AR for iOS / desktop / unsupported devices.
    const ok = await tryActivateAR();
    if (ok) {
      track(Events.ARNativeOpened, { color_id: color.id });
    } else {
      track(Events.ARSimulatedOpened, { color_id: color.id });
      setArOpen(true);
    }
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    track(Events.ShareClicked, { color_id: color.id, color_name: color.name });
    const t = toast.loading("Composing your Story image…");
    try {
      const result = await shareOrDownload(color);
      toast.dismiss(t);
      track(Events.ShareCompleted, { color_id: color.id, method: result.method });
      if (result.method === "shared" || result.method === "downloaded") {
        hapticSuccess();
      }
      if (result.method === "shared") {
        toast.success("Shared. Tag @kisubikashop");
      } else if (result.method === "downloaded") {
        toast.success("Saved. Tap & post to your Story", {
          action: {
            label: "Open Instagram",
            onClick: () => window.open(INSTAGRAM_URL, "_blank", "noopener"),
          },
        });
      } else {
        // cancelled — silent
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error("Couldn’t generate share image");
    } finally {
      setSharing(false);
    }
  };

  const submitDeposit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    hapticTap("medium");
    track(Events.CheckoutSubmitted, {
      color_id: color.id,
      color_name: color.name,
    });
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        color_id: color.id,
        full_name: form.full_name,
        email: form.email,
        shipping_address: form.shipping_address,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[var(--kb-fg)]">
      <Nav />

      <main className="pt-24 sm:pt-28 px-5 sm:px-10 lg:px-14 pb-24">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: bag */}
          <div className="lg:col-span-7 kb-anim-up">
            <div className="flex items-center justify-between mb-4 kb-anim-up kb-delay-1">
              <div>
                <div className="kb-mono-label kb-blue">02 · AR Preview</div>
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">
                  KISUBIKA · <span className="italic kb-blue">{color.name}</span>
                </h1>
              </div>
              <button
                data-testid="share-btn"
                onClick={handleShare}
                disabled={sharing}
                className="kb-btn-ghost !py-3"
              >
                {sharing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Share2 size={14} />
                )}
                Share
              </button>
            </div>

            <BagViewer color={color} onLaunchAR={launchAR} />

            <div className="mt-10">
              <div className="flex items-center justify-between mb-3">
                <div className="kb-mono-label">PALETTE · 20 BLUES</div>
                <div className="kb-mono-label kb-blue">
                  {String(COLORS.findIndex((c) => c.id === color.id) + 1).padStart(
                    2,
                    "0"
                  )}{" "}
                  / 20
                </div>
              </div>
              <ColorPicker
                selected={color}
                onSelect={(c) => {
                  hapticSelection();
                  setColor(c);
                  track(Events.ColorSelected, {
                    color_id: c.id,
                    color_name: c.name,
                  });
                }}
              />
            </div>
          </div>

          {/* Right: actions */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-8 kb-anim-up kb-delay-3">
            <div className="border border-[var(--kb-fg)] p-6 lg:p-8">
              <div className="flex items-center justify-between">
                <div className="kb-mono-label">DEPOSIT</div>
                <div className="kb-mono-label kb-blue">REFUNDABLE</div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-serif text-6xl tracking-tight">£20</span>
                <span className="kb-mono-label text-[var(--kb-fg-2)]">GBP</span>
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-[var(--kb-fg-2)] font-light">
                Reserves your <strong className="text-[var(--kb-fg)] font-medium">{color.name}</strong> tote against
                the next drop. Pay the balance only when your bag is ready
                to ship from London.
              </p>
              <button
                data-testid="open-checkout"
                onClick={() => {
                  hapticTap("medium");
                  track(Events.CheckoutOpened, { color_id: color.id });
                  setCheckoutOpen(true);
                }}
                className="kb-btn-primary w-full mt-6"
              >
                <ShoppingBag size={14} /> Secure my bag
              </button>
            </div>

            <div className="border-t border-[var(--kb-border)] pt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="kb-mono-label text-[var(--kb-fg-2)]">PRINT</div>
                <div className="mt-1 font-serif text-xl">Hand-screened logo</div>
              </div>
              <div>
                <div className="kb-mono-label text-[var(--kb-fg-2)]">MADE IN</div>
                <div className="mt-1 font-serif text-xl">London, UK</div>
              </div>
            </div>

            <a
              data-testid="instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="kb-btn-ghost w-full"
            >
              <Instagram size={14} /> @kisubikashop
            </a>
          </aside>
        </div>

        <SpecSheet color={color} />
      </main>

      <PageFooter />

      {arOpen && <SimulatedAR color={color} onClose={() => setArOpen(false)} />}

      {/* Hidden model-viewer for true scene-anchored AR on Android.
          On iOS / desktop the launchAR handler falls back to SimulatedAR. */}
      <NativeAR color={color} hidden />

      {/* Checkout drawer */}
      {checkoutOpen && (
        <div
          data-testid="checkout-drawer"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 kb-anim-in"
          onClick={() => !submitting && setCheckoutOpen(false)}
          style={{ animationDuration: "220ms" }}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitDeposit}
            className="kb-anim-drawer w-full sm:max-w-lg bg-white border-t sm:border border-[var(--kb-fg)] p-6 sm:p-8"
          >
            {/* Mini bag preview */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-4">
                <div
                  className="relative w-14 h-14 bg-[var(--kb-bg-2)] overflow-hidden border border-[var(--kb-border)]"
                  aria-hidden="true"
                >
                  <div
                    className="kb-bag absolute inset-1"
                    style={{ "--kb-tint": color.filter }}
                  />
                </div>
                <div>
                  <div className="kb-mono-label kb-blue">SECURE YOUR BAG</div>
                  <h2 className="font-serif text-2xl mt-0.5 leading-none">
                    £20 deposit
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !submitting && setCheckoutOpen(false)}
                aria-label="Close"
                className="p-2 -m-2"
                data-testid="close-checkout"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center justify-between border-y border-[var(--kb-border)] py-3 mb-5">
              <div className="flex items-center gap-3">
                <span
                  className="w-6 h-6 border border-[var(--kb-fg)]"
                  style={{ background: color.hex }}
                />
                <span className="kb-mono-label">{color.name}</span>
              </div>
              <span className="kb-mono-label kb-blue">£20.00 GBP</span>
            </div>

            <label className="block kb-mono-label text-[var(--kb-fg-2)] mt-4 mb-1">
              Full name
            </label>
            <input
              data-testid="checkout-name"
              className="kb-input"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
            />

            <label className="block kb-mono-label text-[var(--kb-fg-2)] mt-4 mb-1">
              Email
            </label>
            <input
              data-testid="checkout-email"
              type="email"
              className="kb-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />

            <label className="block kb-mono-label text-[var(--kb-fg-2)] mt-4 mb-1">
              Shipping address (optional)
            </label>
            <input
              data-testid="checkout-address"
              className="kb-input"
              value={form.shipping_address}
              onChange={(e) =>
                setForm({ ...form, shipping_address: e.target.value })
              }
            />

            <button
              type="submit"
              data-testid="checkout-submit"
              disabled={submitting}
              className="kb-btn-primary w-full mt-7"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Redirecting…
                </>
              ) : (
                <>
                  Pay £20 · Apple Pay or Card
                </>
              )}
            </button>
            <p className="kb-mono-label text-[var(--kb-fg-2)] mt-4 text-center">
              APPLE PAY · CARD · SECURE · STRIPE
            </p>
          </form>
        </div>
      )}
    </div>
  );
};

export default ARPreview;
